/**
 * useEngagementMetrics - Custom hook for engagement metrics data fetching
 * 
 * Wraps useAdminData for engagement metrics API with:
 * - Configurable caching (1-hour cache)
 * - Period parameter support (7d, 30d, 90d, custom)
 * - Tier filter support (all, free, premium, family)
 * - Data transformation for component consumption
 * - Engagement score distribution formatting
 * 
 * Requirements: 3.20
 */

import { useMemo } from 'react';
import { useAdminData } from '../components/shared/useAdminData';
import { API_ENDPOINTS, buildApiUrl } from '../config/api-endpoints';

/**
 * Hook for fetching engagement metrics data
 * 
 * @param {string} period - Time period: '7d', '30d', '90d', or 'custom'
 * @param {string} tier - Subscription tier: 'all', 'free', 'premium', or 'family'
 * @param {object} options - Configuration options
 * @param {string} options.startDate - Start date for custom period (ISO format)
 * @param {string} options.endDate - End date for custom period (ISO format)
 * @returns {object} - { data, loading, error, refresh, fromCache }
 */
export const useEngagementMetrics = (period = '30d', tier = 'all', options = {}) => {
  const {
    startDate = null,
    endDate = null
  } = options;

  // Build query parameters
  const params = useMemo(() => {
    const queryParams = { period, tier };
    
    // Add custom date range if provided
    if (period === 'custom' && startDate && endDate) {
      queryParams.startDate = startDate;
      queryParams.endDate = endDate;
    }
    
    return queryParams;
  }, [period, tier, startDate, endDate]);

  // Transform function to format data for component consumption
  const transform = useMemo(() => (responseData) => {
    if (!responseData || !responseData.success) {
      return null;
    }

    const { data } = responseData;

    // Format data for components
    return {
      // Session metrics
      avgSessionDuration: data.avgSessionDuration || 0,
      avgSessionsPerUser: data.avgSessionsPerUser || 0,
      totalTimeInApp: data.totalTimeInApp || 0,
      avgTimePerUser: data.avgTimePerUser || 0,
      
      // Feature usage (top 10)
      featureUsage: (data.featureUsage || []).map(feature => ({
        featureId: feature.featureId,
        name: feature.name,
        count: feature.count,
        percentage: feature.percentage
      })),
      
      // Engagement score distribution
      engagementScoreDistribution: data.engagementScoreDistribution || {
        '0-0.2': 0,
        '0.2-0.4': 0,
        '0.4-0.6': 0,
        '0.6-0.8': 0,
        '0.8-1.0': 0
      },
      
      // Low engagement users
      lowEngagementUsers: (data.lowEngagementUsers || []).map(user => ({
        userId: user.userId,
        email: user.email,
        lastLogin: user.lastLogin,
        score: user.score
      })),
      
      // Metadata
      period,
      tier,
      startDate: data.startDate || startDate,
      endDate: data.endDate || endDate,
      totalUsers: data.totalUsers || 0
    };
  }, [period, tier, startDate, endDate]);

  // Fetch data using useAdminData with 1-hour cache
  const result = useAdminData(
    `engagement-metrics-${period}-${tier}`,
    buildApiUrl(API_ENDPOINTS.ADMIN.ANALYTICS_HANDLER, '/analytics/engagement-metrics'),
    {
      params,
      ttl: 60 * 60 * 1000, // 1 hour cache
      transform,
      staleWhileRevalidate: true,
      fallbackData: {
        avgSessionDuration: 0,
        avgSessionsPerUser: 0,
        totalTimeInApp: 0,
        avgTimePerUser: 0,
        featureUsage: [],
        engagementScoreDistribution: {
          '0-0.2': 0,
          '0.2-0.4': 0,
          '0.4-0.6': 0,
          '0.6-0.8': 0,
          '0.8-1.0': 0
        },
        lowEngagementUsers: [],
        period,
        tier,
        totalUsers: 0
      }
    }
  );

  return result;
};

/**
 * Helper function to format session duration for display
 * 
 * @param {number} seconds - Duration in seconds
 * @returns {string} - Formatted duration (e.g., "25m 30s")
 */
export const formatSessionDuration = (seconds) => {
  if (!seconds || seconds === 0) {
    return '0s';
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  if (minutes === 0) {
    return `${remainingSeconds}s`;
  }

  if (remainingSeconds === 0) {
    return `${minutes}m`;
  }

  return `${minutes}m ${remainingSeconds}s`;
};

/**
 * Helper function to format time in app for display
 * 
 * @param {number} seconds - Total time in seconds
 * @returns {string} - Formatted time (e.g., "120h 30m")
 */
export const formatTimeInApp = (seconds) => {
  if (!seconds || seconds === 0) {
    return '0h';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours === 0) {
    return `${minutes}m`;
  }

  if (minutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${minutes}m`;
};

/**
 * Helper function to format engagement score for display
 * 
 * @param {number} score - Engagement score (0-1)
 * @returns {string} - Formatted score with color indicator
 */
export const formatEngagementScore = (score) => {
  if (score === null || score === undefined) {
    return { value: 'N/A', color: 'neutral', label: 'No data' };
  }

  const value = (score * 100).toFixed(1);
  let color = 'neutral';
  let label = 'Low';

  if (score >= 0.8) {
    color = 'success';
    label = 'Excellent';
  } else if (score >= 0.6) {
    color = 'success';
    label = 'Good';
  } else if (score >= 0.4) {
    color = 'warning';
    label = 'Moderate';
  } else if (score >= 0.2) {
    color = 'warning';
    label = 'Low';
  } else {
    color = 'error';
    label = 'Very Low';
  }

  return { value: `${value}%`, color, label };
};

/**
 * Helper function to get engagement score distribution chart data
 * 
 * @param {object} distribution - Distribution object from API
 * @returns {object} - Chart.js compatible data
 */
export const getEngagementScoreChartData = (distribution) => {
  if (!distribution) {
    return {
      labels: [],
      datasets: []
    };
  }

  const labels = ['0-20%', '20-40%', '40-60%', '60-80%', '80-100%'];
  const data = [
    distribution['0-0.2'] || 0,
    distribution['0.2-0.4'] || 0,
    distribution['0.4-0.6'] || 0,
    distribution['0.6-0.8'] || 0,
    distribution['0.8-1.0'] || 0
  ];

  const backgroundColors = [
    'rgba(239, 68, 68, 0.6)',   // Red (0-20%)
    'rgba(245, 158, 11, 0.6)',  // Orange (20-40%)
    'rgba(234, 179, 8, 0.6)',   // Yellow (40-60%)
    'rgba(34, 197, 94, 0.6)',   // Green (60-80%)
    'rgba(16, 185, 129, 0.6)'   // Dark Green (80-100%)
  ];

  return {
    labels,
    datasets: [{
      label: 'Number of Users',
      data,
      backgroundColor: backgroundColors,
      borderColor: backgroundColors.map(color => color.replace('0.6', '1')),
      borderWidth: 1
    }]
  };
};

/**
 * Helper function to get feature usage chart data
 * 
 * @param {array} featureUsage - Feature usage array from API
 * @returns {object} - Chart.js compatible data
 */
export const getFeatureUsageChartData = (featureUsage) => {
  if (!featureUsage || featureUsage.length === 0) {
    return {
      labels: [],
      datasets: []
    };
  }

  const labels = featureUsage.map(f => f.name);
  const data = featureUsage.map(f => f.count);
  const percentages = featureUsage.map(f => f.percentage);

  return {
    labels,
    datasets: [{
      label: 'Usage Count',
      data,
      backgroundColor: 'rgba(59, 130, 246, 0.6)',
      borderColor: 'rgba(59, 130, 246, 1)',
      borderWidth: 1
    }],
    percentages // For tooltip display
  };
};

/**
 * Helper function to validate period and tier parameters
 * 
 * @param {string} period - Time period
 * @param {string} tier - Subscription tier
 * @returns {object} - { valid: boolean, error: string|null }
 */
export const validateEngagementParams = (period, tier) => {
  const validPeriods = ['7d', '30d', '90d', 'custom'];
  const validTiers = ['all', 'free', 'premium', 'family'];

  if (!validPeriods.includes(period)) {
    return { 
      valid: false, 
      error: `Invalid period: ${period}. Must be one of: ${validPeriods.join(', ')}` 
    };
  }

  if (!validTiers.includes(tier)) {
    return { 
      valid: false, 
      error: `Invalid tier: ${tier}. Must be one of: ${validTiers.join(', ')}` 
    };
  }

  return { valid: true, error: null };
};

/**
 * Helper function to calculate engagement metrics summary
 * 
 * @param {object} metrics - Engagement metrics data
 * @returns {object} - Summary statistics
 */
export const calculateEngagementSummary = (metrics) => {
  if (!metrics) {
    return {
      totalSessions: 0,
      totalTimeSpent: 0,
      avgEngagementScore: 0,
      activeUsers: 0,
      lowEngagementCount: 0
    };
  }

  const distribution = metrics.engagementScoreDistribution || {};
  const totalUsers = Object.values(distribution).reduce((sum, count) => sum + count, 0);
  
  // Calculate weighted average engagement score
  const scoreRanges = {
    '0-0.2': 0.1,
    '0.2-0.4': 0.3,
    '0.4-0.6': 0.5,
    '0.6-0.8': 0.7,
    '0.8-1.0': 0.9
  };
  
  let weightedSum = 0;
  Object.entries(distribution).forEach(([range, count]) => {
    weightedSum += (scoreRanges[range] || 0) * count;
  });
  
  const avgEngagementScore = totalUsers > 0 ? weightedSum / totalUsers : 0;

  return {
    totalSessions: Math.round(metrics.avgSessionsPerUser * totalUsers),
    totalTimeSpent: metrics.totalTimeInApp,
    avgEngagementScore,
    activeUsers: totalUsers,
    lowEngagementCount: metrics.lowEngagementUsers?.length || 0
  };
};

export default useEngagementMetrics;
